import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Backdrop, Grow, Modal } from '@material-ui/core';
import Config from '../../commons/config/Config.js';
import { ICONO_CONFIRMAR, ICONO_CANCELAR } from '../../commons/dialog/Dialog.js';

/**
 * VEC-3318 — despachar o rechazar VARIAS líneas de una vez.
 *
 * DOS DECISIONES DE ALCANCE, tomadas con el PO y visibles en la UI:
 *
 * 1. **El despacho masivo es solo POR COMPRA.** Transferir exige elegir de qué depósito sale cada
 *    línea, y el depósito que tiene 10 del ítem A puede tener 0 del ítem B: un único selector de
 *    origen para una selección mezclada sería mentira. La transferencia queda de a una línea, donde
 *    se puede elegir el origen correcto y ver su disponible. El modal lo dice, para que nadie
 *    busque la opción que no está.
 *
 * 2. **Se procesa línea por línea y se sigue ante un fallo.** Una puede quedarse sin stock o haber
 *    sido cerrada por otro usuario mientras tanto; frenar todo por eso obligaría a repetir el resto.
 *    Al final se informa cuántas salieron y cuántas no. Lo que se pudo hacer, queda hecho.
 *
 * Es N llamadas al endpoint de a una y no un endpoint masivo nuevo: cada línea es una transacción
 * independiente en el backend —con su movimiento y su efecto sobre el stock—, y un endpoint que las
 * agrupara tendría que decidir si son una transacción sola, que es justamente lo que NO se quiere.
 *
 * ESTRUCTURA BOOTSTRAP, NO `DialogTitle`/`DialogContent`/`DialogActions`.
 *
 * Esos tres traen sus PROPIOS paddings (`MuiDialogTitle-root`, `MuiDialogContent-root`,
 * `MuiDialogActions-root`) y se aplican ENCIMA de `modal-header` / `modal-body` / `modal-footer`,
 * así que el modal quedaba con márgenes distintos a los ~95 modales Bootstrap del sistema. Es lo que
 * advierte docs/design-system/dialogos-y-modales.md ("Evitar Dialog / Modal de Material-UI para
 * modales nuevos") y lo mismo que hizo descartar la primera versión de GridHistoryModal.
 *
 * `Modal` de MUI queda SOLO como contenedor —backdrop, portal y foco—, igual que en
 * TicketsGridExport. La caja la arma Bootstrap, y por eso el modal se alinea arriba y mide lo que
 * miden los demás.
 */

const BASE_URL = '/inventario/solicitudes/detalles';

/**
 * Tope de la observación. Es el `maxLength` del textarea Y el denominador del contador, así que el
 * número vive en UN solo lugar: mostrar "/ 255" y dejar escribir 300 es peor que no mostrar nada.
 * Coincide con el `varchar(255)` de `item_stock_movimientos.motivo`.
 */
const MAX_MOTIVO = 255;

const ACCION_DESPACHAR = 'despachar';
const ACCION_RECHAZAR = 'rechazar';

const InventarioSolicitudesMasivaModal = (props) => {
  const intl = useIntl();

  const accion = props.accion;

  /**
   * El lote se CONGELA al abrir.
   *
   * Si se leyera `props.detalles` en vivo, al avisarle a la grilla lo procesado la selección se
   * achicaría y el modal se quedaría sin las líneas sobre las que acaba de informar: el resultado
   * desaparecería justo cuando hay que leerlo.
   */
  const [lote, setLote] = useState([]);
  const [motivo, setMotivo] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [resultado, setResultado] = useState(null);

  /**
   * La observación del despacho arranca ESCRITA con "Despacho masivo".
   *
   * Es un valor de verdad, no un `placeholder`: se guarda tal cual si nadie lo toca, y se puede
   * borrar o reemplazar. Un placeholder gris no se guardaría, y entonces el historial de una tanda de
   * veinte líneas quedaría sin ninguna pista de que se despacharon juntas.
   *
   * Solo en DESPACHO. El rechazo arranca vacío a propósito: su motivo es obligatorio y tiene que
   * decir POR QUÉ no se entrega — un texto por defecto ahí lo volvería un trámite, que es justo lo
   * que el campo intenta evitar.
   */
  const textoInicial = (accionActual) =>
    accionActual === ACCION_RECHAZAR
      ? ''
      : intl.formatMessage({
          id: 'inventarioSolicitudes.masiva.observacion_default',
          defaultMessage: 'Despacho masivo',
        });

  useEffect(() => {
    if (!accion) return;
    setLote(props.detalles || []);
    setMotivo(textoInicial(accion));
    setProcesando(false);
    setProgreso(0);
    setResultado(null);
  }, [accion]);

  const detalles = lote;

  const esRechazo = accion === ACCION_RECHAZAR;
  const puedeConfirmar = () => !procesando && detalles.length > 0 && (!esRechazo || motivo.trim() !== '');

  /**
   * Una llamada por línea, EN SERIE.
   *
   * En serie y no en paralelo a propósito: varias líneas pueden competir por el stock del mismo
   * ítem, y en paralelo el resultado dependería del orden en que responda el servidor. En serie el
   * reporte es reproducible y el usuario ve el avance.
   *
   * `fetch` directo y no `Utils.sendData` porque ese muestra un Swal por CADA error: con cinco
   * líneas fallando serían cinco carteles encima del modal. Acá los errores se juntan y se informan
   * una vez.
   */
  const procesar = async () => {
    if (!puedeConfirmar()) return;

    setProcesando(true);
    setProgreso(0);

    const fallos = [];
    const idsOk = [];

    for (let i = 0; i < detalles.length; i++) {
      const detalle = detalles[i];
      const url = Config.get('apiUrlBase') + BASE_URL + '/' + detalle.id + '/' + (esRechazo ? 'cancelar' : 'despachar');
      // El motivo es OBLIGATORIO al rechazar y OPCIONAL al despachar: rechazar sin explicar por qué
      // deja un pedido sin resolver y sin razón, mientras que un despacho normal se explica solo.
      // Vacío no se manda: el backend lo guardaría como cadena vacía en vez de null.
      const cuerpo = esRechazo ? { motivo: motivo.trim() } : { modo: 'compra' };
      if (!esRechazo && motivo.trim() !== '') cuerpo.motivo = motivo.trim();

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Authorization-Token': localStorage.getItem('token'),
          },
          body: JSON.stringify(cuerpo),
        });

        if (res.ok) {
          idsOk.push(detalle.id);
        } else {
          fallos.push(etiquetaDe(detalle));
        }
      } catch (e) {
        fallos.push(etiquetaDe(detalle));
      }

      setProgreso(i + 1);
    }

    setProcesando(false);
    setResultado({ exitos: idsOk.length, fallos: fallos });

    // Se avisa SIEMPRE, aunque haya fallos: lo que salió bien ya está escrito y la grilla tiene que
    // reflejarlo. El modal queda abierto mostrando el resultado.
    props.onProcesado(idsOk);
  };

  const etiquetaDe = (detalle) => {
    const item = detalle.item || {};
    return (item.numeroDeParte ? item.numeroDeParte + ' · ' : '') + (item.descripcion || detalle.id);
  };

  if (!accion) return null;

  return (
    <Modal open={!!accion} onClose={procesando ? undefined : props.onClose} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 300 }}>
      <Grow in={!!accion}>
        <div tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header bg-fleet">
                <h4 className="modal-title text-white mb-0">
                  <i className={'fa align-middle icon-modal-title ' + (esRechazo ? 'fa-thumbs-down' : 'fa-truck')}></i>
                  {esRechazo ? (
                    <FormattedMessage id="inventarioSolicitudes.masiva.titulo_rechazar" defaultMessage=" Rechazar solicitudes" />
                  ) : (
                    <FormattedMessage id="inventarioSolicitudes.masiva.titulo_despachar" defaultMessage=" Despachar por compra" />
                  )}
                </h4>
                {/* La × de cerrar: va en TODOS los modales del sistema y es la salida
                    que la gente busca primero. */}
                {/* La × también queda inerte mientras procesa, igual que el backdrop y el botón de
                    volver: el bucle sigue corriendo aunque el modal se desmonte, así que cerrar acá
                    dejaba al usuario sin el resultado ni la lista de las que fallaron. */}
                <button
                  type="button"
                  className="close"
                  aria-label="Close"
                  disabled={procesando}
                  onClick={procesando ? undefined : props.onClose}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div className="modal-body modal-grey modal-mh ovf-x-hidden">
                {resultado ? (
                  <div>
                    <div className={resultado.fallos.length === 0 ? 'alert alert-success' : 'alert alert-warning'}>
                      <FormattedMessage
                        id="inventarioSolicitudes.masiva.resultado"
                        defaultMessage="{exitos} de {total} procesadas."
                        values={{ exitos: <b>{resultado.exitos}</b>, total: detalles.length }}
                      />
                    </div>
                    {resultado.fallos.length > 0 && (
                      <div>
                        {/* Cuáles fallaron, por nombre: un contador solo obligaría a adivinar cuáles
                            reintentar. */}
                        <div className="mb-1">
                          <FormattedMessage
                            id="inventarioSolicitudes.masiva.fallidas"
                            defaultMessage="No se pudieron procesar:"
                          />
                        </div>
                        <ul className="mb-0 pl-4">
                          {resultado.fallos.map((f, i) => (
                            <li key={i}>{f}</li>
                          ))}
                        </ul>
                        <small className="text-muted">
                          <FormattedMessage
                            id="inventarioSolicitudes.masiva.fallidas_ayuda"
                            defaultMessage="Probá de a una para ver el motivo de cada caso."
                          />
                        </small>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="mb-3">
                      <FormattedMessage
                        id="inventarioSolicitudes.masiva.cantidad"
                        defaultMessage="Vas a procesar {n, plural, one {# solicitud} other {# solicitudes}}."
                        values={{ n: detalles.length }}
                      />
                    </div>

                    {!esRechazo && (
                      <div className="alert alert-info">
                        <FormattedMessage
                          id="inventarioSolicitudes.masiva.solo_compra"
                          defaultMessage="Se despacha todo lo pendiente de cada una COMO COMPRA: entra stock nuevo y no se descuenta de ningún depósito. Para transferir desde otro depósito hacé el despacho de a una, así podés elegir de dónde sale cada repuesto."
                        />
                      </div>
                    )}

                    {/* El MISMO campo para las dos acciones, cambiando solo si es obligatorio: al rechazar
                        hace falta la razón —un pedido que no se entrega y no se explica queda sin resolver
                        para el que lo pidió—, y al despachar es una observación que a veces suma (de qué
                        compra salió, a quién se le pidió) y a veces no. */}
                    <div className="form-group mb-0">
                      <label htmlFor="masivaMotivo">
                        {esRechazo ? (
                          <>
                            <FormattedMessage id="inventarioSolicitudes.masiva.motivo" defaultMessage="Motivo" />
                            <span className="text-danger"> *</span>
                          </>
                        ) : (
                          <FormattedMessage
                            id="inventarioSolicitudes.masiva.observacion"
                            defaultMessage="Observación (opcional)"
                          />
                        )}
                      </label>
                      <textarea
                        id="masivaMotivo"
                        className="form-control"
                        rows={3}
                        maxLength={MAX_MOTIVO}
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                      />
                      {/* Cuánto queda por escribir. `maxLength` ya frena en seco, pero sin contador
                          el usuario descubre el tope recién cuando el campo deja de responder —y ahí
                          no sabe si se rompió algo—. */}
                      <small className="text-muted float-right">
                        {motivo.length} / {MAX_MOTIVO}
                      </small>
                      <small className="text-muted">
                        {/* Que el texto es el MISMO para todas no es obvio y cambia lo que uno escribe. */}
                        <FormattedMessage
                          id="inventarioSolicitudes.masiva.motivo_ayuda"
                          defaultMessage="Se registra el mismo texto en todas las solicitudes seleccionadas."
                        />
                      </small>
                    </div>

                    {procesando && (
                      <div className="mt-3 text-muted">
                        <FormattedMessage
                          id="inventarioSolicitudes.masiva.progreso"
                          defaultMessage="Procesando {hechas} de {total}…"
                          values={{ hechas: progreso, total: detalles.length }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer modal-grey">
                {resultado ? (
                  <button type="button" className="btn btn-fleet" onClick={props.onClose}>
                    <FormattedMessage id="inventarioSolicitudes.masiva.cerrar" defaultMessage="Cerrar" />
                  </button>
                ) : (
                  <>
                    {/* Confirmar a la izquierda, con check; volver a la derecha, rojo y con la cruz. Ver
                        docs/design-system/dialogos-y-modales.md. */}
                    <button type="button" className="btn btn-primary" onClick={procesar} disabled={!puedeConfirmar()}>
                      <i className={ICONO_CONFIRMAR + ' mr-1'} />
                      {esRechazo ? (
                        <FormattedMessage id="inventarioSolicitudes.masiva.confirmar_rechazo" defaultMessage="Rechazar" />
                      ) : (
                        <FormattedMessage id="inventarioSolicitudes.masiva.confirmar_despacho" defaultMessage="Despachar" />
                      )}
                    </button>
                    <button type="button" className="btn btn-danger" onClick={props.onClose} disabled={procesando}>
                      <i className={ICONO_CANCELAR + ' mr-1'} />
                      <FormattedMessage id="inventarioSolicitudes.masiva.volver" defaultMessage="Volver" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Grow>
    </Modal>
  );
};

export { ACCION_DESPACHAR, ACCION_RECHAZAR };
export default InventarioSolicitudesMasivaModal;
